import { supabase } from '@/lib/supabase';
import { transformGooglePlaceForDB, validatePlaceData } from '@/utils/googlePlacesTransform';

/**
 * Enhanced camp database service with proper Google Places transformation
 */

/**
 * Save Google Places data to database with proper transformation
 * @param {Object} googlePlaceData - Raw Google Places API result
 */
export const saveGooglePlaceToDatabase = async (googlePlaceData) => {
  try {
    console.group('💾 Saving Google Place to Database');
    console.log('📥 Raw Google Place Data:', googlePlaceData);
    
    // Transform the Google Place data to database format
    const transformedData = transformGooglePlaceForDB(googlePlaceData);
    
    // Check if camp already exists by place_id
    console.log('🔍 Checking for existing record:', transformedData.place_id);
    
    const { data: existingArray, error: checkError } = await supabase
      .from('camp_audit_data')
      .select('id, place_id, view_count, save_count, click_count, popularity_score')
      .eq('place_id', transformedData.place_id);
      // ✅ NO .single() - returns array instead
    
    if (checkError) {
      console.error('❌ Error checking existing record:', checkError);
      throw checkError;
    }
    
    // ✅ Extract first record or null (no PGRST116 error)
    const existing = existingArray && existingArray.length > 0 
      ? existingArray[0] 
      : null;
    
    if (existing) {
      console.log('✅ Found existing record, will update');
    } else {
      console.log('✅ No existing record, will insert new');
    }
    
    if (existing) {
      // Update existing record, preserve engagement metrics
      const updateData = {
        ...transformedData,
        // Preserve existing engagement metrics
        view_count: existing.view_count,
        save_count: existing.save_count,
        click_count: existing.click_count,
        // Update popularity score based on current data
        popularity_score: transformedData.popularity_score
      };
      
      // Remove fields that shouldn't be updated
      delete updateData.audit_id;
      delete updateData.created_at;
      
      const { data, error } = await supabase
        .from('camp_audit_data')
        .update(updateData)
        .eq('place_id', transformedData.place_id)
        .select();
      
      if (error) throw error;
      
      console.log('✅ Updated existing camp:', transformedData.name);
      console.groupEnd();
      return data[0];
      
    } else {
      // Insert new record
      const { data, error } = await supabase
        .from('camp_audit_data')
        .insert(transformedData)
        .select();
      
      if (error) throw error;
      
      console.log('✅ Inserted new camp:', transformedData.name);
      console.groupEnd();
      return data[0];
    }
    
  } catch (error) {
    console.error('❌ Failed to save Google Place to database:', error);
    console.error('❌ Error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    console.groupEnd();
    throw error;
  }
};

/**
 * Batch save multiple Google Places results
 * @param {Array} googlePlacesArray - Array of Google Places API results
 */
export const batchSaveGooglePlaces = async (googlePlacesArray) => {
  console.log(`💾 Batch saving ${googlePlacesArray.length} Google Places to database...`);
  
  const results = [];
  const errors = [];
  
  for (const [index, place] of googlePlacesArray.entries()) {
    try {
      console.log(`📝 Processing ${index + 1}/${googlePlacesArray.length}: ${place.name || place.place_id}`);
      const result = await saveGooglePlaceToDatabase(place);
      results.push(result);
    } catch (error) {
      console.error(`❌ Failed to save place ${index + 1}:`, error);
      errors.push({
        place: place,
        error: error.message,
        index: index
      });
    }
  }
  
  console.log(`✅ Batch save complete: ${results.length} successful, ${errors.length} failed`);
  
  if (errors.length > 0) {
    console.warn('⚠️ Errors encountered:', errors);
  }
  
  return {
    successful: results,
    errors: errors,
    summary: {
      total: googlePlacesArray.length,
      successful: results.length,
      failed: errors.length
    }
  };
};

/**
 * Search camps in database with enhanced filtering
 */
export const searchCampsInDatabase = async (searchParams) => {
  const { location, radius, categories, query } = searchParams;
  
  try {
    console.log('🔍 Searching database cache...', {
      hasLocation: !!location,
      hasRadius: !!radius,
      hasQuery: !!query,
      hasCategories: !!(categories && categories.length > 0),
      query: query?.substring(0, 50) // Log first 50 chars of query
    });
    
    let dbQuery = supabase
      .from('camp_audit_data')
      .select('*');
    
    // Location-based search (within radius)
    if (location && radius) {
      const radiusInDegrees = radius / 69; // Rough conversion: 1 degree ≈ 69 miles
      
      console.log('📍 Applying location filter:', {
        lat: location.lat,
        lng: location.lng,
        radius: radius,
        radiusInDegrees: radiusInDegrees
      });
      
      dbQuery = dbQuery
        .gte('coordinates_lat', location.lat - radiusInDegrees)
        .lte('coordinates_lat', location.lat + radiusInDegrees)
        .gte('coordinates_lng', location.lng - radiusInDegrees)
        .lte('coordinates_lng', location.lng + radiusInDegrees);
    }
    
    // Category filtering
    if (categories && categories.length > 0) {
      console.log('🏷️ Applying category filter:', categories);
      dbQuery = dbQuery.overlaps('all_categories', categories);
    }
    
    // Text search
    if (query) {
      console.log('🔤 Applying text search:', query.substring(0, 50));
      try {
        dbQuery = dbQuery.textSearch('search_vector', query);
      } catch (textSearchError) {
        console.error('❌ Text search error:', textSearchError);
        // If text search fails (e.g., search_vector column doesn't exist), 
        // fall back to ILIKE search on name and location
        console.log('⚠️ Falling back to ILIKE search');
        dbQuery = dbQuery.or(`name.ilike.%${query}%,location.ilike.%${query}%`);
      }
    }
    
    // REMOVED: Freshness filter - show all camps from database regardless of age
    // Previously filtered by: data_freshness_days <= 30
    // This ensures that any camp stored in the database will display in search results
    
    // Order by popularity
    dbQuery = dbQuery.order('popularity_score', { ascending: false });
    
    // Limit results for performance
    dbQuery = dbQuery.limit(100);
    
    const { data, error } = await dbQuery;
    
    if (error) {
      console.error('❌ Database query error:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }
    
    console.log(`✅ Found ${data?.length || 0} camps in database cache`);
    
    return data || [];
    
  } catch (error) {
    console.error('❌ Database search failed:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code,
      stack: error.stack
    });
    
    // Return empty array but log the error for debugging
    return [];
  }
};

/**
 * Track user engagement with a camp
 */
export const trackCampEngagement = async (placeId, engagementType) => {
  try {
    console.log(`📊 Tracking ${engagementType} for camp: ${placeId}`);
    
    // Insert interaction record
    const { error: interactionError } = await supabase
      .from('user_camp_interactions')
      .insert({
        place_id: placeId,
        interaction_type: engagementType,
        user_context: {
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent
        }
      });
    
    if (interactionError) {
      console.warn('⚠️ Failed to insert interaction:', interactionError);
      // Don't throw - this is not critical
    }
    
    // Update camp engagement counters
    const { error: updateError } = await supabase.rpc('increment_camp_engagement', {
      p_place_id: placeId,
      p_field: `${engagementType}_count`
    });
    
    if (updateError) {
      console.warn('⚠️ Failed to update engagement counter:', updateError);
      // Don't throw - this is not critical
    }
    
    console.log(`✅ Tracked ${engagementType} for camp ${placeId}`);
    
  } catch (error) {
    console.error('❌ Failed to track engagement:', error);
    // Don't throw - engagement tracking is not critical
  }
};

/**
 * Get camps needing refresh (stale data)
 */
export const getCampsNeedingRefresh = async () => {
  try {
    const { data, error } = await supabase.rpc('get_camps_needing_refresh');
    
    if (error) throw error;
    
    console.log(`🔄 Found ${data?.length || 0} camps needing refresh`);
    return data || [];
    
  } catch (error) {
    console.error('❌ Failed to get camps needing refresh:', error);
    return [];
  }
};

/**
 * Test database connection and schema
 */
export const testDatabaseConnection = async () => {
  try {
    console.log('🧪 Testing database connection...');
    
    // Test basic connection
    const { data, error } = await supabase
      .from('camp_audit_data')
      .select('count(*)')
      .limit(1);
    
    if (error) throw error;
    
    console.log('✅ Database connection successful');
    
    // Test schema by trying to insert a test record
    const testData = {
      place_id: `TEST-${Date.now()}`,
      name: 'Test Camp Connection',
      location: 'Test Location',
      coordinates_lat: 0,
      coordinates_lng: 0,
      audit_id: `TEST-AUDIT-${Date.now()}`,
      audit_date: new Date().toISOString(),
      audit_year: new Date().getFullYear(),
      audit_week: 1,
      api_compliant: true,
      ethical_source: true,
      source: 'test'
    };
    
    const { data: insertData, error: insertError } = await supabase
      .from('camp_audit_data')
      .insert(testData)
      .select();
    
    if (insertError) throw insertError;
    
    // Clean up test record
    await supabase
      .from('camp_audit_data')
      .delete()
      .eq('place_id', testData.place_id);
    
    console.log('✅ Database schema test successful');
    return true;
    
  } catch (error) {
    console.error('❌ Database test failed:', error);
    return false;
  }
};
